import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
} from 'reactstrap';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import useAuth from 'hooks/useAuth';
import useTheme from 'hooks/useTheme';
import dayjs from 'dayjs';
import { dashboardService } from '../../../../services/dashboardService';
import { getOperationTimezone } from '../../../../utils/timezone';
import { apiErrorMessage } from '../../../../utils/apiErrorMessage';
import { MetricSkeleton, ErrorState, KpiCard } from '../../../../components/dashboard/shared/DashboardShared';
import DateRangeFilter from '../../../../components/dateRangeFilter/DateRangeFilter';
import { Table as UiTable, THead, TBody, TR, TH, TD } from '../../../../components/ui/Table';
import { Badge } from '../../../../components/ui/Badge';

const COLOR_PENDING = '#EF4444';
const COLOR_COMMUNE = '#8B5CF6';
const COLOR_CLIENT = '#3B82F6';

// Presets del DateRangeFilter canónico (mismas ventanas que el bloque propio
// anterior: Hoy, 7 días, 30 días, Este mes). Las keys las resuelve rangeFromPreset.
const PB_PRESETS = [
  { key: 'today', quick: true },
  { key: '7d', quick: true },
  { key: '30d', quick: true },
  { key: 'thisMonth', quick: true },
];

/** Agrupa entries planos por la clave primaria, calculando subítems de la clave secundaria. */
export function pivotEntries(entries, mode) {
  const map = new Map();

  for (const entry of entries) {
    const primaryKey = mode === 'municipality' ? entry.municipality : entry.companyId;
    const primaryLabel = mode === 'municipality' ? entry.municipality : entry.businessName;
    const secondaryKey = mode === 'municipality' ? entry.companyId : entry.municipality;
    const secondaryLabel = mode === 'municipality' ? entry.businessName : entry.municipality;

    if (!map.has(primaryKey)) {
      map.set(primaryKey, { key: primaryKey, label: primaryLabel, total: 0, children: [] });
    }
    const group = map.get(primaryKey);
    group.total += entry.count;
    group.children.push({ key: secondaryKey, label: secondaryLabel, count: entry.count });
  }

  return [...map.values()].sort((a, b) => b.total - a.total);
}

const PendingTooltip = ({ active, payload, label }) => {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  return (
    <div className="cv-tooltip">
      <p className="cv-tooltip__label">{label}</p>
      <p className="cv-tooltip__row">
        <span className="cv-tooltip__dot" style={{ background: COLOR_PENDING }} />
        {t('Pendientes')}: <strong>{(payload[0]?.value ?? 0).toLocaleString()}</strong>
      </p>
    </div>
  );
};

const ExpandableRow = ({ group, rank, total, mode }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const pct = total > 0 ? ((group.total / total) * 100).toFixed(1) : '0.0';
  const childLabel = mode === 'municipality' ? t('Cliente') : t('Comuna');

  return (
    <>
      <TR
        className={`cursor-pointer${open ? ' cv-row--open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <TD className="align-middle text-fg-subtle">{rank}</TD>
        <TD className="align-middle font-semibold">
          <i className={`fas fa-chevron-right cv-expand-caret${open ? ' cv-expand-caret--open' : ''} me-2`} />
          {group.label}
        </TD>
        <TD className="align-middle text-right font-semibold">
          {group.total.toLocaleString()}
        </TD>
        <TD className="align-middle text-right">
          <Badge tone="neutral" className="min-w-[52px] justify-center">{pct}%</Badge>
        </TD>
        <TD className="align-middle text-right text-fg-muted">
          {group.children.length} {childLabel}{group.children.length !== 1 ? 's' : ''}
        </TD>
      </TR>
      {open && (
        <TR>
          <TD colSpan={5} className="p-0 border-0">
            <div className="cv-subtable">
              <table>
                <thead>
                  <tr>
                    <th>{childLabel}</th>
                    <th className="text-right">{t('Pendientes')}</th>
                    <th className="text-right">% {t('del grupo')}</th>
                  </tr>
                </thead>
                <tbody>
                  {[...group.children]
                    .sort((a, b) => b.count - a.count)
                    .map((child) => {
                      const childPct =
                        group.total > 0 ? ((child.count / group.total) * 100).toFixed(1) : '0.0';
                      return (
                        <tr key={child.key}>
                          <td>{child.label}</td>
                          <td className="text-right font-semibold">
                            {child.count.toLocaleString()}
                          </td>
                          <td className="text-right text-fg-muted">{childPct}%</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </TD>
        </TR>
      )}
    </>
  );
};

const PendingBreakdownTab = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const logisticCompanyId = useMemo(() => user?.logisticCompany?._id || null, [user]);
  const dashboardIanaTz = useMemo(() => getOperationTimezone(user), [user]);

  const tickColor = isDark ? '#D1D5DB' : '#6B7280';
  const gridColor = isDark ? '#374151' : '#E5E7EB';

  const [startDate, setStartDate] = useState(() => dayjs().subtract(6, 'day').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [activePreset, setActivePreset] = useState('7d'); // key del DateRangeFilter (== rango default 7 días)
  const [mode, setMode] = useState('municipality');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [highlightKey, setHighlightKey] = useState(null);

  const fetchData = useCallback(async () => {
    if (!logisticCompanyId) return;
    setLoading(true);
    setFetchError(null);
    setHighlightKey(null);
    try {
      const res = await dashboardService.getPendingBreakdown(
        logisticCompanyId,
        startDate,
        endDate,
        dashboardIanaTz,
      );
      setData(res.data);
      setHasLoaded(true);
    } catch (err) {
      // Object, not the string itself: apiErrorMessage() can return null and the
      // state doubles as the "there is an error" flag.
      setFetchError({ message: apiErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  }, [logisticCompanyId, startDate, endDate, dashboardIanaTz]);

  const groups = useMemo(
    () => (data?.entries ? pivotEntries(data.entries, mode) : []),
    [data, mode],
  );

  const chartData = useMemo(
    () =>
      groups.slice(0, 15).map((g) => ({
        name: g.label.length > 30 ? g.label.slice(0, 28) + '…' : g.label,
        fullKey: g.key,
        value: g.total,
      })),
    [groups],
  );

  const totalPending = data?.total ?? 0;
  const uniqueMunicipalities = useMemo(
    () => new Set(data?.entries?.map((e) => e.municipality) ?? []).size,
    [data],
  );
  const uniqueClients = useMemo(
    () => new Set(data?.entries?.map((e) => e.companyId) ?? []).size,
    [data],
  );

  const chartHeight = Math.max(260, chartData.length * 42);
  const yAxisWidth = useMemo(() => {
    if (!chartData.length) return 160;
    const maxLen = Math.max(...chartData.map((c) => c.name.length));
    return Math.min(Math.max(maxLen * 6.5, 120), 240);
  }, [chartData]);

  const modeColor = mode === 'municipality' ? COLOR_COMMUNE : COLOR_CLIENT;

  return (
    <div className="cv-tab">
      {/* ── Controles ─────────────────────────────────────────────────────── */}
      <Card className="chart-card mb-3">
        <CardBody className="py-3">
          <div className="cv-controls">
            {/* Rango de fechas (DateRangeFilter canónico) */}
            <div className="cv-controls__group">
              <span className="cv-controls__label">{t('Período')}</span>
              <DateRangeFilter
                value={activePreset}
                onChange={(s, e, p) => { setStartDate(s); setEndDate(e); setActivePreset(p); }}
                presets={PB_PRESETS}
              />
            </div>

            <div className="cv-controls__divider" />

            {/* Toggle de agrupación */}
            <div className="cv-controls__group cv-controls__group--field">
              <span className="cv-controls__label">{t('Agrupar por')}</span>
              <div className="d-flex" style={{ gap: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid #D1D5DB' }}>
                <button
                  className={`cv-preset-btn${mode === 'municipality' ? ' cv-preset-btn--active' : ''}`}
                  style={{ borderRadius: 0, flex: 1 }}
                  onClick={() => setMode('municipality')}
                >
                  <i className="fas fa-map-marker-alt me-1" />
                  {t('Comuna')}
                </button>
                <button
                  className={`cv-preset-btn${mode === 'client' ? ' cv-preset-btn--active' : ''}`}
                  style={{ borderRadius: 0, flex: 1, borderLeft: '1px solid #D1D5DB' }}
                  onClick={() => setMode('client')}
                >
                  <i className="fas fa-building me-1" />
                  {t('Cliente')}
                </button>
              </div>
            </div>

            {/* Botón */}
            <div className="cv-controls__group cv-controls__group--action">
              <span className="cv-controls__label">&nbsp;</span>
              <button
                className="cv-consult-btn"
                onClick={fetchData}
                disabled={loading}
              >
                <i className={`fas ${loading ? 'fa-sync-alt spinning' : 'fa-search'}`} />
                {loading ? t('Cargando…') : t('Consultar')}
              </button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* ── Estado vacío / error ──────────────────────────────────────────── */}
      {!hasLoaded && !loading && !fetchError && (
        <div className="cv-empty-state">
          <i className="fas fa-clock" />
          <p>
            {t('Selecciona un rango de fechas y haz clic en')} <strong>{t('Consultar')}</strong> {t('para ver los pedidos pendientes por')} {mode === 'municipality' ? t('comuna') : t('cliente')}.
          </p>
        </div>
      )}
      {fetchError && !loading && <ErrorState message={fetchError.message} onRetry={fetchData} />}

      {/* ── Contenido ─────────────────────────────────────────────────────── */}
      {(hasLoaded || loading) && (
        <>
          {/* KPI cards */}
          <Row className="mb-3 g-3">
            {[
              {
                title: t('Total Pendientes'),
                value: totalPending.toLocaleString(),
                subtitle: `${startDate} → ${endDate}`,
                color: COLOR_PENDING,
                icon: 'fas fa-clock',
              },
              {
                title: t('Comunas con pendientes'),
                value: uniqueMunicipalities.toLocaleString(),
                subtitle: t('destinos distintos'),
                color: COLOR_COMMUNE,
                icon: 'fas fa-map-marker-alt',
              },
              {
                title: t('Clientes con pendientes'),
                value: uniqueClients.toLocaleString(),
                subtitle: t('remitentes distintos'),
                color: COLOR_CLIENT,
                icon: 'fas fa-building',
              },
            ].map((kpi) => (
              <Col key={kpi.title} xs="12" md="4">
                <KpiCard {...kpi} loading={loading} />
              </Col>
            ))}
          </Row>

          {/* Gráfico de barras horizontales */}
          <Card className="chart-card mb-3">
            <CardHeader className="chart-card__header d-flex align-items-center justify-content-between">
              <h4 className="chart-card__title mb-0">
                Top {Math.min(15, chartData.length)} {t('por')}{' '}
                <Badge tone="neutral" className="align-middle">
                  <span className="cv-client-dot" style={{ background: modeColor }} aria-hidden />
                  {mode === 'municipality' ? t('Comuna') : t('Cliente')}
                </Badge>
              </h4>
              {!loading && groups.length > 0 && (
                <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                  {groups.length} {mode === 'municipality' ? t('comunas') : t('clientes')} {t('en total')}
                </span>
              )}
            </CardHeader>
            <CardBody>
              {loading ? (
                <div className="skeleton-shimmer" style={{ height: 320, borderRadius: 8 }} />
              ) : chartData.length === 0 ? (
                <div className="cv-empty-state">
                  <i className="fas fa-inbox" />
                  <p>{t('No hay pendientes para el rango seleccionado.')}</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <BarChart
                    data={chartData}
                    layout="vertical"
                    margin={{ top: 4, right: 48, left: 0, bottom: 4 }}
                    barSize={22}
                    onClick={(e) => {
                      if (e?.activePayload?.[0]) {
                        const key = e.activePayload[0].payload.fullKey;
                        setHighlightKey((prev) => (prev === key ? null : key));
                      }
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={gridColor} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: tickColor }} tickLine={false} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={yAxisWidth}
                      tick={{ fontSize: 11, fill: tickColor }}
                      tickLine={false}
                    />
                    <Tooltip content={<PendingTooltip />} />
                    <Bar dataKey="value" name={t('Pendientes')} radius={[0, 4, 4, 0]}>
                      {chartData.map((entry) => (
                        <Cell
                          key={entry.fullKey}
                          fill={
                            highlightKey === null
                              ? modeColor
                              : highlightKey === entry.fullKey
                              ? COLOR_PENDING
                              : `${modeColor}55`
                          }
                          cursor="pointer"
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardBody>
          </Card>

          {/* Tabla expandible */}
          {!loading && groups.length > 0 && (
            <Card className="chart-card">
              <CardHeader className="chart-card__header d-flex align-items-center justify-content-between">
                <h4 className="chart-card__title mb-0">
                  {t('Detalle por')} {mode === 'municipality' ? t('Comuna') : t('Cliente')}
                </h4>
                <span className="text-muted" style={{ fontSize: '0.78rem' }}>
                  {t('Haz clic en una fila para expandir el desglose')}
                </span>
              </CardHeader>
              <CardBody className="p-0 cv-tblwrap">
                <UiTable>
                  <THead>
                    <TR>
                      <TH className="w-10">#</TH>
                      <TH>{mode === 'municipality' ? t('Comuna') : t('Cliente')}</TH>
                      <TH className="text-right">{t('Pendientes')}</TH>
                      <TH className="text-right">% {t('del total')}</TH>
                      <TH className="text-right">{mode === 'municipality' ? t('Clientes') : t('Comunas')}</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {(highlightKey
                      ? groups.filter((g) => g.key === highlightKey)
                      : groups
                    ).map((group, i) => (
                      <ExpandableRow
                        key={group.key}
                        group={group}
                        rank={highlightKey ? groups.findIndex((g) => g.key === group.key) + 1 : i + 1}
                        total={totalPending}
                        mode={mode}
                      />
                    ))}
                  </TBody>
                </UiTable>
                {highlightKey && (
                  <div className="text-center py-2">
                    <button
                      className="cv-preset-btn"
                      onClick={() => setHighlightKey(null)}
                    >
                      <i className="fas fa-times me-1" />
                      {t('Ver todos')}
                    </button>
                  </div>
                )}
              </CardBody>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

// memo: no recibe props; lo aísla de los re-renders del dashboard padre.
export default React.memo(PendingBreakdownTab);
